import { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, AlertCircle, FileText, Calendar, CreditCard, Check, CheckSquare, Square, Share2, Loader2, WifiOff, CloudOff, Download, Lock, Printer, CheckCircle2, Banknote, Droplets } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import axios from "axios";
import { toast } from "sonner";
import { AgentPaymentButton, isAgentLoggedIn } from "@/components/AgentPaymentFlow";
import html2canvas from "html2canvas";
import LocationSaver from "@/components/LocationSaver";
import {
  isOfflineModeValid,
  getOfflineBills,
  getAdminFeeOffline,
  saveOfflinePayment,
  updateBillStatusOffline,
  getTodayDateString
} from "@/utils/offlineDB";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CustomerBills() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const customerId = searchParams.get("id");
  const forceOfflineMode = searchParams.get("offline") === "true";
  
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adminFeePerBill, setAdminFeePerBill] = useState(1500);
  const [isSharing, setIsSharing] = useState(false);
  
  // Offline mode state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [offlineModeActive, setOfflineModeActive] = useState(false);
  
  // Ref untuk capture screenshot
  const billsContainerRef = useRef(null);
  const receiptRef = useRef(null);
  
  // State untuk memilih tagihan (angsuran) - khusus petugas
  const [selectedBillIds, setSelectedBillIds] = useState([]);
  const userRole = localStorage.getItem("user_role") || "";
  const userName = localStorage.getItem("user_name") || "";
  const isPetugas = userRole.toLowerCase() === "petugas";
  
  // State untuk dialog password dan struk offline
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [uangDiterima, setUangDiterima] = useState("");
  const [paymentMethod, setPaymentMethod] = useState(""); // "cash" or "transfer"
  const [paymentMethodError, setPaymentMethodError] = useState("");
  const [offlinePaymentResult, setOfflinePaymentResult] = useState(null);
  const [receiptSettings, setReceiptSettings] = useState(null);
  const [rawbtPrintUrl, setRawbtPrintUrl] = useState("");

  // Load receipt settings on mount
  useEffect(() => {
    const loadReceiptSettings = async () => {
      try {
        const res = await axios.get(`${API}/receipt-settings`);
        setReceiptSettings(res.data);
      } catch (err) {
        console.log("Using default receipt settings");
      }
    };
    loadReceiptSettings();
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!customerId) {
      navigate("/");
      return;
    }

    // Wajib login guard (defense-in-depth jika URL diakses langsung)
    const checkAuthAndLoad = async () => {
      try {
        const authRes = await axios.get(`${API}/auth-settings`);
        const requireLogin = authRes.data.require_login_for_bills !== false;
        const userToken = localStorage.getItem("user_token");
        const customerToken = localStorage.getItem("customer_token");
        const isLoggedIn = !!(userToken || customerToken);
        if (requireLogin && !isLoggedIn) {
          toast.error("Silakan login terlebih dahulu untuk melihat tagihan");
          navigate("/login");
          return;
        }
      } catch (err) {
        console.error("Auth settings check failed:", err);
      }
      loadBillsData();
    };
    checkAuthAndLoad();
  }, [customerId, isOffline, forceOfflineMode]);

  // Load bills - from API if online, from IndexedDB if offline
  const loadBillsData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Check if user wants to use offline mode (from URL parameter or no internet)
      const useOfflineMode = (isOffline || forceOfflineMode) && isPetugas;
      
      if (useOfflineMode) {
        // Check if offline mode is valid for today
        const offlineStatus = await isOfflineModeValid();
        
        if (offlineStatus.valid) {
          setOfflineModeActive(true);
          
          // Load from IndexedDB
          const offlineBills = await getOfflineBills(customerId);
          const offlineFee = await getAdminFeeOffline();
          
          // Filter only unpaid bills and sort by periode (oldest first)
          const unpaidBills = offlineBills
            .filter(b => b.status === 'unpaid')
            .sort((a, b) => {
              // Parse bulan like "Des 2025", "Jan 2026", "Agust 2025", etc.
              // Support multiple formats: "Agu", "Agust", "Agustus", etc.
              const monthNames = {
                'Jan': 1, 'Januari': 1,
                'Feb': 2, 'Februari': 2,
                'Mar': 3, 'Maret': 3,
                'Apr': 4, 'April': 4,
                'Mei': 5, 'May': 5,
                'Jun': 6, 'Juni': 6,
                'Jul': 7, 'Juli': 7,
                'Agu': 8, 'Agust': 8, 'Agustus': 8,
                'Sep': 9, 'Sept': 9, 'September': 9,
                'Okt': 10, 'Oktober': 10,
                'Nop': 11, 'Nov': 11, 'November': 11,
                'Des': 12, 'Desember': 12
              };
              
              const parseDate = (bulanStr) => {
                if (!bulanStr) return { year: 9999, month: 12 };
                const parts = bulanStr.trim().split(' ');
                if (parts.length < 2) return { year: 9999, month: 12 };
                const monthNum = monthNames[parts[0]] || 12;
                const year = parseInt(parts[1]) || 9999;
                return { year, month: monthNum };
              };
              
              const dateA = parseDate(a.bulan);
              const dateB = parseDate(b.bulan);
              
              // Sort by year first, then by month (oldest first)
              if (dateA.year !== dateB.year) return dateA.year - dateB.year;
              return dateA.month - dateB.month;
            });
          
          if (unpaidBills.length === 0 && offlineBills.length > 0) {
            setBills([]);
            toast.info("Tidak ada tagihan yang belum dibayar (Mode Offline)");
          } else if (unpaidBills.length === 0) {
            setError("ID Pelanggan tidak ditemukan dalam data offline");
          } else {
            setBills(unpaidBills);
            setAdminFeePerBill(offlineFee);
            toast.info(`Mode Offline - ${unpaidBills.length} tagihan ditemukan`);
          }
        } else {
          setError("Mode offline tidak aktif. Silakan download data terlebih dahulu saat online.");
          setOfflineModeActive(false);
        }
      } else {
        // Online mode - fetch from API
        setOfflineModeActive(false);
        await fetchBills();
        await fetchFee();
      }
    } catch (err) {
      console.error("Error loading bills:", err);
      setError("Gagal memuat data tagihan");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFee = async () => {
    try {
      const res = await axios.get(`${API}/admin/fees/pdam`);
      setAdminFeePerBill(res.data.fee || 1500);
    } catch (err) {
      console.log("Using default fee");
    }
  };

  const fetchBills = async () => {
    try {
      const response = await axios.get(`${API}/customer/bills?no_samb=${customerId}`);
      setBills(response.data.bills || []);
      if (response.data.message) {
        toast.info(response.data.message);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setError("ID Pelanggan tidak ditemukan dalam sistem");
      } else {
        setError("Gagal memuat data tagihan");
      }
      toast.error(err.response?.data?.detail || "Terjadi kesalahan");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const styles = {
      unpaid: "status-unpaid",
      pending: "status-pending",
      paid: "status-paid",
    };
    const labels = {
      unpaid: "Belum Bayar",
      pending: "Menunggu Verifikasi",
      paid: "Lunas",
    };
    return <span className={styles[status]}>{labels[status]}</span>;
  };

  // Calculate totals
  const unpaidBills = bills.filter(b => b.status === "unpaid");
  const pendingBills = bills.filter(b => b.status === "pending");
  
  // Untuk petugas: hitung berdasarkan tagihan yang dipilih
  // Untuk customer/agen: hitung semua tagihan unpaid
  const isPetugasLoggedIn = isPetugas && isAgentLoggedIn();
  const billsToPayList = isPetugasLoggedIn 
    ? unpaidBills.filter(b => selectedBillIds.includes(b.id))
    : unpaidBills;
  
  const totalTagihan = billsToPayList.reduce((sum, b) => sum + b.jumlah, 0);
  const totalAdminFee = billsToPayList.length * adminFeePerBill;
  const totalBayar = totalTagihan + totalAdminFee;
  
  // Toggle pilih tagihan
  const handleToggleBill = (billId) => {
    setSelectedBillIds(prev => 
      prev.includes(billId) 
        ? prev.filter(id => id !== billId) 
        : [...prev, billId]
    );
  };

  // Share tagihan sebagai gambar
  const handleShare = async () => {
    if (!billsContainerRef.current) {
      toast.error("Tidak dapat membuat gambar tagihan");
      return;
    }
    
    setIsSharing(true);
    toast.info("Menyiapkan gambar tagihan...");
    
    try {
      // Capture the bills container as canvas
      const canvas = await html2canvas(billsContainerRef.current, {
        backgroundColor: '#e0f2fe', // sky-100 background
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
        windowWidth: billsContainerRef.current.scrollWidth,
        windowHeight: billsContainerRef.current.scrollHeight,
      });
      
      // Convert canvas to blob
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });
      
      const customerName = bills[0]?.nama || "Pelanggan";
      const fileName = `Tagihan_PDAM_${customerId}_${customerName.replace(/\s+/g, '_')}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });
      
      // Check if Web Share API supports sharing files
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Tagihan PDAM - ${customerName}`,
          text: `Tagihan PDAM PERUMDA TIRTA MUARO TEBO\nID Pelanggan: ${customerId}`,
          files: [file],
        });
        toast.success("Gambar tagihan berhasil dibagikan!");
      } else {
        // Fallback: Download the image
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("Gambar tagihan berhasil diunduh!");
      }
    } catch (err) {
      console.error("Error sharing:", err);
      if (err.name !== "AbortError") {
        toast.error("Gagal membuat gambar tagihan");
      }
    } finally {
      setIsSharing(false);
    }
  };
  
  // Pilih semua tagihan
  const handleSelectAll = () => {
    if (selectedBillIds.length === unpaidBills.length) {
      setSelectedBillIds([]);
    } else {
      setSelectedBillIds(unpaidBills.map(b => b.id));
    }
  };

  const handlePayAll = () => {
    if (unpaidBills.length === 0) {
      toast.error("Tidak ada tagihan yang perlu dibayar");
      return;
    }
    const billIds = unpaidBills.map(b => b.id).join(",");
    navigate(`/payment?ids=${billIds}&total=${totalBayar}`);
  };

  // Handle offline payment - buka dialog password dulu
  const [offlinePaymentProcessing, setOfflinePaymentProcessing] = useState(false);
  
  const handleOfflinePayment = () => {
    if (billsToPayList.length === 0) {
      toast.error("Pilih tagihan yang akan dibayar");
      return;
    }
    
    // Reset state dan buka dialog password
    setPassword("");
    setPasswordError("");
    setUangDiterima("");
    setPaymentMethod("");
    setPaymentMethodError("");
    setShowPasswordDialog(true);
  };
  
  // Konfirmasi pembayaran offline setelah password benar
  const handleConfirmOfflinePayment = async () => {
    // Validasi metode pembayaran (wajib dipilih)
    if (!paymentMethod) {
      setPaymentMethodError("Pilih metode pembayaran");
      return;
    }
    
    if (!password.trim()) {
      setPasswordError("Masukkan password");
      return;
    }
    
    setOfflinePaymentProcessing(true);
    setPasswordError("");
    setPaymentMethodError("");
    
    try {
      // Jika ada koneksi internet, verifikasi password dengan backend
      // Jika tidak ada koneksi (true offline), skip verifikasi karena data sudah divalidasi saat download
      const phone = localStorage.getItem("user_phone");
      
      if (navigator.onLine && phone) {
        try {
          await axios.post(`${API}/auth/login`, { phone, password });
        } catch (verifyErr) {
          const msg = verifyErr.response?.data?.detail || "Verifikasi gagal";
          if (msg.includes("password") || msg.includes("salah") || msg.includes("Invalid")) {
            setPasswordError("Password salah");
          } else {
            setPasswordError(msg);
          }
          setOfflinePaymentProcessing(false);
          return;
        }
      }
      // Jika tidak ada internet, kita percaya user sudah login dengan benar sebelumnya
      
      // Password benar atau offline tanpa internet - proses pembayaran offline
      const paymentData = {
        bill_ids: billsToPayList.map(b => b.id),
        customer_no: customerId,
        customer_name: bills[0]?.nama || "",
        customer_address: bills[0]?.alamat || "",
        amount: totalTagihan,
        admin_fee: totalAdminFee,
        total: totalBayar,
        payment_method: paymentMethod, // "cash" atau "transfer"
        bill_details: billsToPayList.map(b => ({
          bulan: b.bulan,
          harga_air: b.harga_air || 0,
          adm: b.adm || 0,
          denda: b.denda || 0,
          admin_fee: adminFeePerBill,
          total: b.jumlah + adminFeePerBill
        }))
      };
      
      const savedPayment = await saveOfflinePayment(paymentData);
      
      if (savedPayment) {
        // Mark bills as paid locally
        for (const billId of billsToPayList.map(b => b.id)) {
          await updateBillStatusOffline(billId, 'paid');
        }
        
        // Hitung kembalian
        const uangDiterimaNum = parseInt(uangDiterima) || 0;
        const kembalianCalc = uangDiterimaNum > 0 ? uangDiterimaNum - totalBayar : null;
        
        // Buat hasil pembayaran untuk struk
        const paymentResult = {
          batch_id: savedPayment.id,
          no_sambungan: customerId,
          nama: bills[0]?.nama || "",
          alamat: bills[0]?.alamat || "",
          results: billsToPayList.map(b => ({
            periode: b.bulan,
            total_bayar: b.jumlah + adminFeePerBill,
            detail: {
              stan_lalu: b.stan_lalu,
              stan_sekarang: b.stan_sekarang,
              pakai: b.pakai
            }
          })),
          total_tagihan: totalTagihan,
          total_admin: totalAdminFee,
          grand_total: totalBayar,
          jumlah_bulan: billsToPayList.length,
          kasir: userName,
          tanggal: new Date().toLocaleString("id-ID"),
          timestamp: new Date().toLocaleString("id-ID"),
          payment_method: paymentMethod, // "cash" atau "transfer"
          uang_diterima: uangDiterimaNum > 0 ? uangDiterimaNum : null,
          kembalian: kembalianCalc !== null && kembalianCalc >= 0 ? kembalianCalc : null,
          is_offline: true
        };
        
        // Tutup dialog password dan tampilkan struk
        setShowPasswordDialog(false);
        setOfflinePaymentResult(paymentResult);
        setSelectedBillIds([]);
        
        toast.success(`${billsToPayList.length} tagihan berhasil dibayar (Offline)`);
      } else {
        toast.error("Gagal menyimpan pembayaran offline");
      }
    } catch (err) {
      console.error("Offline payment error:", err);
      toast.error("Terjadi kesalahan saat menyimpan pembayaran offline");
    } finally {
      setOfflinePaymentProcessing(false);
    }
  };
  
  // Reset struk dan reload data
  const resetOfflinePayment = async () => {
    setOfflinePaymentResult(null);
    setRawbtPrintUrl("");
    await loadBillsData();
  };
  
  // Generate plain text receipt for thermal printer (58mm)
  const generateReceiptText = (paymentData) => {
    const data = paymentData || offlinePaymentResult;
    if (!data) return "";
    const divider = "--------------------------------";
    const formatRp = (n) => `Rp${(n || 0).toLocaleString('id-ID')}`;
    const headerText = receiptSettings?.header_text || "PDAM TIRTA MUARO TEBO";
    const showFee = receiptSettings?.show_fee_loket !== false;
    const footerText = receiptSettings?.footer_text || "Total sudah termasuk biaya adm";
    
    let receipt = "";
    receipt += `     ${headerText}\n`;
    receipt += "     Struk Pembayaran\n";
    receipt += divider + "\n";
    receipt += `Tgl   : ${data.tanggal || data.timestamp}\n`;
    receipt += divider + "\n";
    receipt += `No.Samb  : ${data.no_sambungan}\n`;
    receipt += `Nama     : ${data.nama}\n`;
    if (receiptSettings?.show_customer_address !== false && data.alamat) {
      receipt += `Alamat   : ${data.alamat}\n`;
    }
    receipt += divider + "\n";
    
    // List each bill - without stan details
    data.results?.forEach((result) => {
      receipt += `Periode  : ${result.periode}\n`;
      receipt += `SUBTOTAL : ${formatRp(result.total_bayar)}\n`;
      receipt += divider + "\n";
    });
    
    receipt += `TOTAL BAYAR   : ${formatRp(data.grand_total)}\n`;
    receipt += divider + "\n";
    
    if (data.uang_diterima) {
      receipt += `Uang Diterima : ${formatRp(data.uang_diterima)}\n`;
      receipt += `Kembalian     : ${formatRp(data.kembalian || 0)}\n`;
      receipt += divider + "\n";
    }
    
    receipt += `Status : LUNAS\n`;
    if (data.payment_method) {
      const methodLabel = data.payment_method.toLowerCase() === 'cash' ? 'Cash' : 'Transfer';
      receipt += `Metode : ${methodLabel}\n`;
    }
    if (receiptSettings?.show_kasir !== false) {
      receipt += `Kasir  : ${data.kasir}\n`;
    }
    receipt += divider + "\n";
    receipt += `${footerText}\n`;
    receipt += `        STRUK INI ADALAH\n`;
    receipt += `      PEMBAYARAN YANG SAH\n`;
    receipt += `       by:produk_digital\n`;
    receipt += "\n\n\n";
    
    return receipt;
  };
  
  // Generate RawBT URL when offlinePaymentResult changes
  useEffect(() => {
    if (offlinePaymentResult) {
      const receiptText = generateReceiptText(offlinePaymentResult);
      if (receiptText) {
        const base64 = btoa(unescape(encodeURIComponent(receiptText)));
        setRawbtPrintUrl(`rawbt:base64,${base64}`);
      }
    } else {
      setRawbtPrintUrl("");
    }
  }, [offlinePaymentResult, receiptSettings]);
  
  // Print via RawBT app - simplified handler for toast notification
  const handlePrintClick = () => {
    toast.info("Membuka RawBT... Pastikan aplikasi RawBT sudah terinstall dan printer terhubung.");
  };
  
  // Share receipt as image
  const handleShareReceipt = async () => {
    if (!receiptRef.current) return;
    
    setIsSharing(true);
    toast.info("Menyiapkan struk...");
    
    try {
      const canvas = await html2canvas(receiptRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false
      });
      
      const blob = await new Promise((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', 0.9);
      });
      
      const fileName = `Struk_Offline_PDAM_${customerId}_${Date.now()}.jpg`;
      const file = new File([blob], fileName, { type: 'image/jpeg' });
      
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `Struk Pembayaran PDAM (Offline)`,
          files: [file],
        });
        toast.success("Struk berhasil dibagikan!");
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("Struk berhasil diunduh!");
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Share error:", err);
        toast.error("Gagal membuat struk");
      }
    } finally {
      setIsSharing(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient">
      <header className="container mx-auto px-6 py-6">
        <nav className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/icons/icon-72x72.png" alt="Logo" className="w-10 h-10 rounded-xl" />
            <span className="text-xl font-heading font-semibold text-slate-800">Produk Digital</span>
          </div>
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              Kembali
            </Button>
          </Link>
        </nav>
      </header>

      <main className="container mx-auto px-6 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-3xl mx-auto">
            {/* Password Confirmation Dialog for Offline Payment */}
            <AnimatePresence>
              {showPasswordDialog && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
                  onClick={(e) => { if (e.target === e.currentTarget && !offlinePaymentProcessing) setShowPasswordDialog(false); }}
                >
                  <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
                  >
                    <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 text-center">
                      <Lock className="w-8 h-8 mx-auto mb-1" />
                      <h3 className="font-bold text-lg">Konfirmasi Pembayaran Offline</h3>
                      <p className="text-sm opacity-90">Masukkan password untuk melanjutkan</p>
                    </div>
                    <div className="p-5 space-y-4">
                      {/* Payment summary */}
                      <div className="bg-slate-50 rounded-xl p-3 text-sm space-y-1">
                        <div className="flex justify-between">
                          <span className="text-slate-600">Pelanggan</span>
                          <span className="font-semibold">{bills[0]?.nama || "-"}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-600">Jumlah Tagihan</span>
                          <span className="font-semibold">{billsToPayList.length} bulan</span>
                        </div>
                        <div className="flex justify-between border-t border-slate-200 pt-1 mt-1">
                          <span className="font-semibold text-slate-800">Total Bayar</span>
                          <span className="font-bold text-amber-600">{formatCurrency(totalBayar)}</span>
                        </div>
                      </div>

                      {/* Payment Method Selection - REQUIRED */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-2 block">
                          Metode Pembayaran <span className="text-red-500">*</span>
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            type="button"
                            onClick={() => { setPaymentMethod("cash"); setPaymentMethodError(""); }}
                            className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                              paymentMethod === "cash" 
                                ? "border-emerald-500 bg-emerald-50 text-emerald-700" 
                                : "border-slate-200 bg-white text-slate-600 hover:border-emerald-300"
                            }`}
                            data-testid="payment-method-cash"
                            disabled={offlinePaymentProcessing}
                          >
                            <Banknote className="w-5 h-5" />
                            <span className="font-semibold">Cash</span>
                            {paymentMethod === "cash" && <Check className="w-4 h-4" />}
                          </button>
                          <button
                            type="button"
                            onClick={() => { setPaymentMethod("transfer"); setPaymentMethodError(""); }}
                            className={`flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-all ${
                              paymentMethod === "transfer" 
                                ? "border-blue-500 bg-blue-50 text-blue-700" 
                                : "border-slate-200 bg-white text-slate-600 hover:border-blue-300"
                            }`}
                            data-testid="payment-method-transfer"
                            disabled={offlinePaymentProcessing}
                          >
                            <CreditCard className="w-5 h-5" />
                            <span className="font-semibold">Transfer</span>
                            {paymentMethod === "transfer" && <Check className="w-4 h-4" />}
                          </button>
                        </div>
                        {paymentMethodError && (
                          <p className="text-red-500 text-xs mt-1" data-testid="payment-method-error">{paymentMethodError}</p>
                        )}
                      </div>

                      {/* Password input */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">Password</label>
                        <Input
                          type="password"
                          placeholder="Masukkan password Anda"
                          value={password}
                          onChange={(e) => { setPassword(e.target.value); setPasswordError(""); }}
                          onKeyDown={(e) => { if (e.key === "Enter") handleConfirmOfflinePayment(); }}
                          className={`h-12 rounded-xl ${passwordError ? "border-red-400" : ""}`}
                          data-testid="offline-password-input"
                          disabled={offlinePaymentProcessing}
                          autoFocus
                        />
                        {passwordError && (
                          <p className="text-red-500 text-xs mt-1" data-testid="password-error">{passwordError}</p>
                        )}
                      </div>

                      {/* Uang Diterima input */}
                      <div>
                        <label className="text-sm font-medium text-slate-700 mb-1 block">
                          <Banknote className="w-4 h-4 inline mr-1" />
                          Uang Diterima (opsional)
                        </label>
                        <Input
                          type="tel"
                          inputMode="numeric"
                          placeholder="Masukkan jumlah uang dari pelanggan"
                          value={uangDiterima ? parseInt(uangDiterima).toLocaleString('id-ID') : ''}
                          onChange={(e) => {
                            const val = e.target.value.replace(/[^0-9]/g, '');
                            setUangDiterima(val);
                          }}
                          className="h-12 rounded-xl"
                          data-testid="uang-diterima-input"
                          disabled={offlinePaymentProcessing}
                        />
                        {uangDiterima && parseInt(uangDiterima) > 0 && (
                          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex justify-between text-sm">
                              <span className="text-green-700">Kembalian</span>
                              <span className={`font-bold ${parseInt(uangDiterima) >= totalBayar ? 'text-green-600' : 'text-red-600'}`}>
                                {parseInt(uangDiterima) >= totalBayar
                                  ? formatCurrency(parseInt(uangDiterima) - totalBayar)
                                  : `Kurang ${formatCurrency(totalBayar - parseInt(uangDiterima))}`
                                }
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-3 pt-2">
                        <Button
                          variant="outline"
                          className="flex-1 h-12 rounded-xl"
                          onClick={() => setShowPasswordDialog(false)}
                          disabled={offlinePaymentProcessing}
                          data-testid="cancel-offline-btn"
                        >
                          Batal
                        </Button>
                        <Button
                          className="flex-1 h-12 rounded-xl bg-gradient-to-r from-amber-500 to-orange-600"
                          onClick={handleConfirmOfflinePayment}
                          disabled={offlinePaymentProcessing || !password.trim()}
                          data-testid="confirm-offline-btn"
                        >
                          {offlinePaymentProcessing ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                              Memproses...
                            </>
                          ) : (
                            "Bayar Sekarang"
                          )}
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Offline Payment Receipt */}
            <AnimatePresence>
              {offlinePaymentResult && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                >
                  <Card className="glass-card-solid mb-6 overflow-hidden">
                    <div ref={receiptRef} className="bg-white">
                      <div className="bg-gradient-to-r from-amber-500 to-orange-600 text-white p-4 text-center">
                        {receiptSettings?.logo_url ? (
                          <div className="flex justify-center mb-2">
                            <img 
                              src={receiptSettings.logo_url} 
                              alt="Logo" 
                              style={{
                                maxWidth: '80px',
                                maxHeight: '80px',
                                width: 'auto',
                                height: 'auto',
                                objectFit: 'contain',
                                borderRadius: '8px',
                                backgroundColor: 'white',
                                padding: '4px'
                              }}
                            />
                          </div>
                        ) : (
                          <Droplets className="w-10 h-10 mx-auto mb-2" />
                        )}
                        <h2 className="text-lg font-bold" data-testid="receipt-header">
                          {receiptSettings?.header_text || "PRODUK DIGITAL"}
                        </h2>
                        <p className="text-sm opacity-90">Struk Pembayaran</p>
                      </div>
                      
                      <CardContent className="p-4">
                        {/* Transaction info - Only date, no transaction ID */}
                        <div className="border-b border-dashed border-slate-200 pb-3 mb-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Tanggal</span>
                            <span className="font-medium text-slate-800">{offlinePaymentResult.tanggal}</span>
                          </div>
                        </div>
                        
                        {/* Customer info */}
                        <div className="border-b border-dashed border-slate-200 pb-3 mb-3">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-500">No. Sambungan</span>
                            <span className="font-mono font-semibold">{offlinePaymentResult.no_sambungan}</span>
                          </div>
                          <p className="font-semibold text-slate-800">{offlinePaymentResult.nama}</p>
                          {receiptSettings?.show_customer_address !== false && offlinePaymentResult.alamat && (
                            <p className="text-xs text-slate-500">{offlinePaymentResult.alamat}</p>
                          )}
                        </div>
                        
                        {/* Bill details per period - without stan details */}
                        <div className="space-y-3 mb-4">
                          {offlinePaymentResult.results?.map((result, idx) => (
                            <div key={idx} className="border border-slate-200 rounded-lg p-3" data-testid={`receipt-bill-${idx}`}>
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="text-xs text-slate-500">Periode</p>
                                  <p className="font-semibold text-slate-800">{result.periode}</p>
                                </div>
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">LUNAS</span>
                              </div>
                              
                              <div className="space-y-1 text-sm">
                                <div className="flex justify-between pt-1 border-t border-slate-100 font-bold">
                                  <span className="text-slate-700 text-base">Subtotal</span>
                                  <span className="text-amber-600 text-base">{formatCurrency(result.total_bayar)}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Grand Total */}
                        <div className="bg-slate-50 rounded-xl p-3">
                          {receiptSettings?.show_total_tagihan !== false && (
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-600">Total Tagihan</span>
                              <span className="font-medium">{formatCurrency(offlinePaymentResult.total_tagihan)}</span>
                            </div>
                          )}
                          {receiptSettings?.show_fee_loket !== false && (
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-slate-600">Total Biaya Admin ({offlinePaymentResult.jumlah_bulan} x {formatCurrency(adminFeePerBill)})</span>
                              <span className="font-medium">{formatCurrency(offlinePaymentResult.total_admin)}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                            <span className="font-bold text-slate-800">Total Bayar</span>
                            <span className="text-xl font-bold text-amber-600">
                              {formatCurrency(offlinePaymentResult.grand_total)}
                            </span>
                          </div>
                        </div>
                        
                        {/* Status and Kasir */}
                        <div className="mt-3 pt-3 border-t border-dashed border-slate-200">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-slate-500">Status</span>
                            <span className="font-semibold text-green-600 flex items-center gap-1">
                              LUNAS <CheckCircle2 className="w-4 h-4" />
                            </span>
                          </div>
                          {/* Payment Method */}
                          {offlinePaymentResult.payment_method && (
                            <div className="flex justify-between text-sm mb-1">
                              <span className="text-slate-500">Metode Bayar</span>
                              <span className={`font-semibold flex items-center gap-1 ${offlinePaymentResult.payment_method?.toLowerCase() === 'cash' ? 'text-emerald-600' : 'text-blue-600'}`}>
                                {offlinePaymentResult.payment_method?.toLowerCase() === 'cash' ? (
                                  <><Banknote className="w-4 h-4" /> Cash</>
                                ) : (
                                  <><CreditCard className="w-4 h-4" /> Transfer</>
                                )}
                              </span>
                            </div>
                          )}
                          {receiptSettings?.show_kasir !== false && offlinePaymentResult.kasir && (
                            <div className="flex justify-between text-sm">
                              <span className="text-slate-500">Kasir</span>
                              <span className="font-semibold text-slate-800">
                                {offlinePaymentResult.kasir}
                              </span>
                            </div>
                          )}
                          {offlinePaymentResult.uang_diterima && (
                            <>
                              <div className="flex justify-between text-sm mt-2">
                                <span className="text-slate-500">Uang Diterima</span>
                                <span className="font-medium text-slate-800">{formatCurrency(offlinePaymentResult.uang_diterima)}</span>
                              </div>
                              <div className="flex justify-between text-sm mt-1">
                                <span className="text-slate-500 font-semibold">Kembalian</span>
                                <span className="font-bold text-green-600">{formatCurrency(offlinePaymentResult.kembalian || 0)}</span>
                              </div>
                            </>
                          )}
                        </div>
                        
                        <p className="text-center text-xs text-slate-400 mt-4">
                          {receiptSettings?.footer_text || "Terima kasih atas pembayaran Anda"}
                        </p>
                        <p className="text-center text-xs text-slate-400">
                          {receiptSettings?.note_text || "Struk ini adalah bukti pembayaran yang sah"}
                        </p>
                      </CardContent>
                    </div>
                    
                    <div className="p-4 bg-slate-50 space-y-3">
                      <div className="flex gap-3">
                        {/* Use <a> tag directly with href for RawBT deep link - more reliable on Android */}
                        <a 
                          href={rawbtPrintUrl}
                          onClick={handlePrintClick}
                          className="flex-1 inline-flex items-center justify-center gap-2 h-10 px-4 py-2 rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground text-sm font-medium"
                          data-testid="print-offline-receipt-btn"
                        >
                          <Printer className="w-4 h-4" />
                          Print Struk
                        </a>
                        <Button 
                          variant="outline" 
                          className="flex-1 gap-2"
                          onClick={handleShareReceipt}
                          disabled={isSharing}
                          data-testid="share-offline-receipt-btn"
                        >
                          {isSharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                          Share Struk
                        </Button>
                      </div>
                      <Button 
                        className="w-full gap-2 bg-gradient-to-r from-amber-500 to-orange-600"
                        onClick={resetOfflinePayment}
                        data-testid="new-offline-search-btn"
                      >
                        <FileText className="w-4 h-4" />
                        Cek Tagihan Lain
                      </Button>
                    </div>
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Offline Mode Banner */}
            {offlineModeActive && !offlinePaymentResult && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-center gap-3">
                <WifiOff className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-semibold text-amber-800">Mode Offline Aktif</p>
                  <p className="text-sm text-amber-600">Data dari tanggal {getTodayDateString()}. Pembayaran akan disinkronkan saat online.</p>
                </div>
              </div>
            )}
            
            {/* Main Content - Hidden when showing offline receipt */}
            {!offlinePaymentResult && (
            <>
            <div className="mb-8">
              <div className="flex items-center justify-between mb-2">
                <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900">
                  Tagihan Pelanggan
                </h1>
                {bills.length > 0 && unpaidBills.length > 0 && !offlineModeActive && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleShare}
                    disabled={isSharing}
                    className="gap-2 rounded-full border-sky-200 text-sky-600 hover:bg-sky-50"
                    data-testid="share-btn"
                  >
                    {isSharing ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Share2 className="w-4 h-4" />
                    )}
                    {isSharing ? "Menyiapkan..." : "Share"}
                  </Button>
                )}
              </div>
              <p className="text-sky-700 font-semibold text-sm mb-1">
                PERUMDA TIRTA MUARO TEBO
              </p>
              <p className="text-slate-600">
                ID Pelanggan: <span className="font-semibold text-sky-600">{customerId}</span>
              </p>
            </div>

            {isLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="glass-card-solid">
                    <CardContent className="p-6">
                      <Skeleton className="h-6 w-32 mb-4" />
                      <Skeleton className="h-4 w-full mb-2" />
                      <Skeleton className="h-4 w-2/3" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : error ? (
              <div className="glass-card rounded-2xl p-12 text-center">
                {isOffline && isPetugas ? (
                  <>
                    <CloudOff className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                    <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
                      Mode Offline Tidak Tersedia
                    </h2>
                    <p className="text-slate-600 mb-6">{error}</p>
                    <Link to="/offline-mode">
                      <Button className="rounded-full btn-flow gap-2">
                        <Download className="w-4 h-4" />
                        Download Data Offline
                      </Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                    <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
                      Tidak Ditemukan
                    </h2>
                    <p className="text-slate-600 mb-6">{error}</p>
                    <Link to="/">
                      <Button className="rounded-full btn-flow">
                        Coba ID Lain
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            ) : bills.length === 0 ? (
              <div className="glass-card rounded-2xl p-12 text-center">
                <FileText className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-xl font-heading font-semibold text-slate-800 mb-2">
                  Tidak Ada Tagihan
                </h2>
                <p className="text-slate-600 mb-6">
                  Semua tagihan sudah lunas. Terima kasih!
                </p>
                <Link to={`/history?id=${customerId}`}>
                  <Button variant="outline" className="rounded-full">
                    Lihat Riwayat Pembayaran
                  </Button>
                </Link>
              </div>
            ) : (
              <>
                <LocationSaver
                  customerId={customerId}
                  customerName={bills[0]?.nama || ""}
                  customerAddress={bills[0]?.alamat || ""}
                  source="PDAM"
                />
                {/* Container untuk share screenshot */}
                <div ref={billsContainerRef} className="bg-sky-100 -mx-6 px-6 py-4 rounded-xl">
                  {/* Header info pelanggan untuk screenshot */}
                  <div className="mb-4 pb-3 border-b border-sky-200">
                    <h2 className="text-lg font-heading font-bold text-slate-900 mb-1">
                      TAGIHAN PERUMDA TIRTA MUARO TEBO
                    </h2>
                    <p className="text-sm text-slate-700">
                      <span className="font-medium">Pelanggan:</span> {bills[0]?.nama || "-"}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-medium">Alamat:</span> {bills[0]?.alamat || "-"}
                    </p>
                    <p className="text-sm text-slate-700">
                      <span className="font-medium">No. ID Pelanggan:</span> <span className="text-sky-600 font-semibold">{customerId}</span>
                    </p>
                  </div>
                  
                  {/* Tampilan pilih tagihan khusus petugas */}
                  {isPetugasLoggedIn && unpaidBills.length > 0 && (
                    <div className="bg-sky-50 border border-sky-200 rounded-xl p-4 mb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-sky-800">Mode Angsuran</p>
                          <p className="text-xs text-sky-600">Pilih tagihan bulan mana saja yang ingin dibayar</p>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={handleSelectAll}
                          className="text-xs border-sky-300 text-sky-700 hover:bg-sky-100"
                          data-testid="select-all-btn"
                        >
                          {selectedBillIds.length === unpaidBills.length ? "Batal Pilih" : "Pilih Semua"}
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-4 mb-4" data-testid="bills-list">
                  {bills.map((bill, index) => {
                    const isSelected = selectedBillIds.includes(bill.id);
                    const canSelect = isPetugasLoggedIn && bill.status === "unpaid";
                    
                    return (
                    <motion.div
                      key={bill.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                    >
                      <Card 
                        className={`bill-card transition-all cursor-pointer ${
                          canSelect 
                            ? isSelected 
                              ? "border-2 border-sky-500 shadow-lg bg-sky-50/50" 
                              : "hover:shadow-lg hover:border-sky-200"
                            : "hover:shadow-lg"
                        }`}
                        onClick={() => canSelect && handleToggleBill(bill.id)}
                        data-testid={`bill-card-${bill.id}`}
                      >
                        <CardContent className="p-5">
                          {/* Nama PDAM */}
                          <div className="mb-3 pb-2 border-b border-slate-100">
                            <p className="text-xs font-semibold text-sky-700">PERUMDA TIRTA MUARO TEBO</p>
                          </div>
                          
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-start gap-3">
                              {/* Checkbox untuk petugas pada tagihan unpaid */}
                              {canSelect && (
                                <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-all ${
                                  isSelected 
                                    ? "bg-sky-500 text-white" 
                                    : "border-2 border-slate-300 bg-white"
                                }`}>
                                  {isSelected && <Check className="w-4 h-4" />}
                                </div>
                              )}
                              <div>
                                <p className="font-heading font-semibold text-slate-800">{bill.nama}</p>
                                <p className="text-sm text-slate-500">{bill.alamat || "Alamat tidak tersedia"}</p>
                              </div>
                            </div>
                            {getStatusBadge(bill.status)}
                          </div>
                          
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <div>
                                <p className="text-xs text-slate-500">Periode</p>
                                <p className="font-medium text-slate-800 text-sm">{bill.bulan}</p>
                              </div>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Denda</p>
                              <p className="font-medium text-red-600 text-sm">{formatCurrency(bill.denda)}</p>
                            </div>
                          </div>
                          
                          <div className="mb-3">
                            <p className="text-xs text-slate-500">No. ID Pelanggan</p>
                            <p className="font-mono text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded inline-block">{bill.no_samb}</p>
                          </div>
                          
                          <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                            <div className="text-xs text-slate-500 space-y-0.5">
                              <div>Harga Air: {formatCurrency(bill.harga_air)}</div>
                              <div>Adm: {formatCurrency(bill.adm)}</div>
                              <div className="text-sky-600 font-medium">Biaya Admin: {formatCurrency(adminFeePerBill)}</div>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-slate-400">Subtotal</p>
                              <p className="text-lg font-heading font-bold text-sky-600">
                                {formatCurrency(bill.jumlah + adminFeePerBill)}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                  })}
                  </div>

                  {/* Total untuk screenshot */}
                  {unpaidBills.length > 0 && (
                    <div className="bg-white rounded-xl p-4 border border-sky-200">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-slate-800">Total Pembayaran ({unpaidBills.length} tagihan)</span>
                        <span className="text-xl font-heading font-bold text-sky-600">
                          {formatCurrency(totalBayar)}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Sudah termasuk biaya admin {formatCurrency(adminFeePerBill)}/tagihan
                      </p>
                    </div>
                  )}
                </div>
                {/* End of screenshot container */}

                {pendingBills.length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 mt-4">
                    <p className="text-amber-800 text-sm">
                      <strong>{pendingBills.length} tagihan</strong> sedang menunggu verifikasi admin.
                    </p>
                  </div>
                )}

                {unpaidBills.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.3 }}
                  >
                    <Card className="glass-card border-2 border-sky-200">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-sky-600" />
                          </div>
                          <div>
                            <h3 className="font-heading font-semibold text-slate-800">
                              Total Pembayaran {offlineModeActive && <span className="text-amber-600 text-sm">(Offline)</span>}
                            </h3>
                            <p className="text-sm text-slate-500">
                              {isPetugasLoggedIn 
                                ? `${billsToPayList.length} dari ${unpaidBills.length} tagihan dipilih`
                                : `${unpaidBills.length} tagihan harus dibayar sekaligus`
                              }
                            </p>
                          </div>
                        </div>

                        {/* Info untuk petugas jika belum pilih tagihan */}
                        {isPetugasLoggedIn && billsToPayList.length === 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
                            <p className="text-amber-800 text-sm text-center">
                              Pilih tagihan di atas untuk melakukan pembayaran angsuran
                            </p>
                          </div>
                        )}

                        {billsToPayList.length > 0 && (
                          <>
                            <div className="bg-slate-50 rounded-xl p-4 mb-4">
                              {/* Detail tagihan yang dipilih untuk petugas */}
                              {isPetugasLoggedIn && (
                                <div className="mb-3 pb-3 border-b border-slate-200">
                                  <p className="text-xs text-slate-500 mb-2">Tagihan yang dipilih:</p>
                                  <div className="flex flex-wrap gap-2">
                                    {billsToPayList.map(b => (
                                      <span key={b.id} className="px-2 py-1 bg-sky-100 text-sky-700 text-xs rounded-full">
                                        {b.bulan}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="flex justify-between items-center">
                                <span className="font-semibold text-slate-800">Total Bayar</span>
                                <span className="text-2xl font-heading font-bold text-sky-600">
                                  {formatCurrency(totalBayar)}
                                </span>
                              </div>
                              <p className="text-xs text-slate-500 mt-1">
                                Sudah termasuk biaya admin {formatCurrency(adminFeePerBill)}/tagihan
                              </p>
                            </div>

                            {/* Offline Payment Button */}
                            {offlineModeActive && isPetugasLoggedIn && (
                              <Button
                                onClick={handleOfflinePayment}
                                disabled={offlinePaymentProcessing}
                                className="w-full h-14 rounded-full text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                                data-testid="offline-pay-btn"
                              >
                                {offlinePaymentProcessing ? (
                                  <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Memproses...
                                  </>
                                ) : (
                                  <>
                                    <WifiOff className="w-5 h-5 mr-2" />
                                    Bayar Offline
                                  </>
                                )}
                              </Button>
                            )}

                            {/* Online Customer Payment */}
                            {!offlineModeActive && !isAgentLoggedIn() && (
                              <Button
                                onClick={handlePayAll}
                                className="w-full h-14 rounded-full text-lg font-semibold btn-flow shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
                                data-testid="pay-all-btn"
                              >
                                Bayar Semua Tagihan
                              </Button>
                            )}

                            {/* Online Agent Payment */}
                            {!offlineModeActive && isAgentLoggedIn() && (
                              <AgentPaymentButton
                                serviceType="pdam"
                                customerNo={customerId}
                                customerName={bills[0]?.nama || ""}
                                customerAddress={bills[0]?.alamat || ""}
                                productName={isPetugasLoggedIn ? `Angsuran PDAM (${billsToPayList.length} bln)` : "Tagihan PDAM"}
                                amount={totalTagihan}
                                adminFee={totalAdminFee}
                                totalOverride={totalBayar}
                                buyerSkuCode="PDAMKAB_TEBO"
                                billIds={billsToPayList.map(b => b.id)}
                                billDetails={billsToPayList.map(b => ({
                                  bulan: b.bulan,
                                  harga_air: b.harga_air || 0,
                                  adm: b.adm || 0,
                                  denda: b.denda || 0,
                                  admin_fee: adminFeePerBill,
                                  total: b.jumlah + adminFeePerBill
                                }))}
                                onSuccess={() => { 
                                  toast.success("Pembayaran berhasil!"); 
                                  setSelectedBillIds([]); // Reset pilihan setelah sukses
                                }}
                                onReceiptClose={() => {
                                  loadBillsData();
                                }}
                              />
                            )}
                          </>
                        )}
                        
                        <p className="text-xs text-center text-slate-500 mt-3">
                          {isPetugasLoggedIn 
                            ? offlineModeActive 
                              ? "* Pembayaran offline akan disinkronkan saat Anda terhubung ke internet"
                              : "* Petugas dapat membayar tagihan secara angsuran"
                            : "* Semua tagihan harus dibayar sekaligus untuk pelunasan"
                          }
                        </p>
                      </CardContent>
                    </Card>
                  </motion.div>
                )}
              </>
            )}
            </>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
